import React, { useEffect, useState, useRef } from 'react';
import { FileJson, CheckCircle, XCircle, AlertCircle, Trash2, Upload } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';

const WorkflowImporter = () => {
  const { isDarkMode } = useTheme();
  const [whatsappFile, setWhatsappFile] = useState(null);
  const [instagramFile, setInstagramFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [whatsappData, setWhatsappData] = useState(null);
  const [instagramData, setInstagramData] = useState(null);
  const [existingWorkflows, setExistingWorkflows] = useState([]);
  const [hasWhatsapp, setHasWhatsapp] = useState(false);
  const [hasInstagram, setHasInstagram] = useState(false);
  const hasAnyWorkflow = existingWorkflows.length > 0;

  // Refs para os inputs de arquivo
  const whatsappInputRef = useRef(null);
  const instagramInputRef = useRef(null);

  // Busca workflows existentes ao montar
  const fetchExistingWorkflows = async () => {
    try {
      const response = await apiService.request('/n8n/workflows', {
        method: 'GET'
      });

      // apiService já retorna o JSON parseado ou lança erro
      // O formato esperado da resposta da API é { success: true, data: { workflows: [], ... } }
      const list = response?.data?.workflows || [];
      setExistingWorkflows(list);
      const types = new Set(
        list.map(w => (w.type || w.metadata?.type || '').toLowerCase()).filter(Boolean)
      );
      setHasWhatsapp(types.has('whatsapp'));
      setHasInstagram(types.has('instagram'));
    } catch (error) {
      console.warn('Erro ao buscar workflows existentes:', error.message);
    }
  };

  useEffect(() => {
    fetchExistingWorkflows();
  }, []);

  const handleDeleteWorkflow = async (workflowId) => {
    const confirmed = window.confirm('Tem certeza que deseja excluir este workflow? Esta ação não pode ser desfeita.');
    if (!confirmed) return;

    try {
      await apiService.request(`/n8n/workflows/${workflowId}`, {
        method: 'DELETE'
      });

      setUploadStatus({ type: 'success', message: 'Workflow excluído com sucesso.' });
      await fetchExistingWorkflows();
    } catch (error) {
      setUploadStatus({ type: 'error', message: error.message || 'Erro ao excluir workflow.' });
    }
  };

  const handleWhatsappFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'application/json') {
      setWhatsappFile(selectedFile);
      setUploadStatus(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          setWhatsappData(jsonData);
        } catch (error) {
          setUploadStatus({
            type: 'error',
            message: 'Arquivo JSON do WhatsApp inválido. Por favor, selecione um arquivo JSON válido.'
          });
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setUploadStatus({
        type: 'error',
        message: 'Por favor, selecione um arquivo JSON para o WhatsApp.'
      });
    }
  };

  const handleInstagramFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'application/json') {
      setInstagramFile(selectedFile);
      setUploadStatus(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          setInstagramData(jsonData);
        } catch (error) {
          setUploadStatus({
            type: 'error',
            message: 'Arquivo JSON do Instagram inválido. Por favor, selecione um arquivo JSON válido.'
          });
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setUploadStatus({
        type: 'error',
        message: 'Por favor, selecione um arquivo JSON para o Instagram.'
      });
    }
  };

  const handleUpload = async () => {
    // Regra: é obrigatório excluir workflows existentes antes de importar
    if (existingWorkflows.length > 0) {
      setUploadStatus({
        type: 'error',
        message: 'Exclua o workflow atual antes de importar um novo.'
      });
      return;
    }

    if (!whatsappData && !instagramData) {
      setUploadStatus({
        type: 'error',
        message: 'Selecione ao menos um arquivo JSON para importar.'
      });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      if (whatsappData && !hasWhatsapp) {
        await apiService.request('/n8n/workflows', {
          method: 'POST',
          body: JSON.stringify({
            name: whatsappData.name || 'Workflow WhatsApp',
            workflow: whatsappData,
            type: 'whatsapp',
            active: true
          })
        });
      }

      if (instagramData && !hasInstagram) {
        await apiService.request('/n8n/workflows', {
          method: 'POST',
          body: JSON.stringify({
            name: instagramData.name || 'Workflow Instagram',
            workflow: instagramData,
            type: 'instagram',
            active: true
          })
        });
      }

      // Atualiza estado e lista
      await fetchExistingWorkflows();
      setUploadStatus({ type: 'success', message: 'Importação concluída. Lista atualizada.' });

      // Limpa seleções
      setWhatsappFile(null);
      setInstagramFile(null);
      setWhatsappData(null);
      setInstagramData(null);
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error.message || 'Erro ao importar workflows.'
      });
    } finally {
      setUploading(false);
    }
  };

  const clearAllFiles = () => {
    setWhatsappFile(null);
    setInstagramFile(null);
    setWhatsappData(null);
    setInstagramData(null);
    setUploadStatus(null);
  };

  return (
    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center mb-4">
        <FileJson className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Importar Workflows n8n
        </h3>
      </div>

      <div className="space-y-4">
        {/* Workflow WhatsApp */}
        <div className="border-b border-gray-200 pb-4">
          <h4 className={`text-md font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Workflow WhatsApp
          </h4>
          <div className="space-y-3">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Selecionar arquivo JSON:
              </label>
              <input
                ref={whatsappInputRef}
                type="file"
                accept=".json"
                onChange={handleWhatsappFileChange}
                className="hidden"
                id="whatsapp-file-input"
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => whatsappInputRef.current?.click()}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${isDarkMode
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Escolher Arquivo
                </button>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {whatsappFile ? whatsappFile.name : 'Nenhum arquivo selecionado'}
                </span>
              </div>
              {hasAnyWorkflow && (
                <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Já existe um workflow importado. Exclua o existente para substituir.
                </p>
              )}
            </div>

            {whatsappData && (
              <div className={`p-3 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h5 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Pré-visualização:
                </h5>
                <div className="space-y-1 text-sm">
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    <strong>Nome:</strong> {whatsappData.name || 'Sem nome'}
                  </p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    <strong>Nós:</strong> {whatsappData.nodes?.length || 0}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workflow Instagram */}
        <div className="border-b border-gray-200 pb-4">
          <h4 className={`text-md font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Workflow Instagram
          </h4>
          <div className="space-y-3">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Selecionar arquivo JSON:
              </label>
              <input
                ref={instagramInputRef}
                type="file"
                accept=".json"
                onChange={handleInstagramFileChange}
                className="hidden"
                id="instagram-file-input"
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => instagramInputRef.current?.click()}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${isDarkMode
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Escolher Arquivo
                </button>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {instagramFile ? instagramFile.name : 'Nenhum arquivo selecionado'}
                </span>
              </div>
              {hasAnyWorkflow && (
                <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Já existe um workflow importado. Exclua o existente para substituir.
                </p>
              )}
            </div>

            {instagramData && (
              <div className={`p-3 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h5 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Pré-visualização:
                </h5>
                <div className="space-y-1 text-sm">
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    <strong>Nome:</strong> {instagramData.name || 'Sem nome'}
                  </p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    <strong>Nós:</strong> {instagramData.nodes?.length || 0}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botões de ação */}
        <div className="flex space-x-3 pt-4">
          <button
            onClick={handleUpload}
            disabled={uploading || (!whatsappData && !instagramData)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${uploading || (!whatsappData && !instagramData)
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            {uploading ? 'Importando...' : 'Importar Workflows'}
          </button>

          {(whatsappFile || instagramFile) && (
            <button
              onClick={clearAllFiles}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${isDarkMode
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Limpar Tudo
            </button>
          )}
        </div>

        {/* Workflows Já Importados */}
        <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <div className="flex items-center mb-2">
            <FileJson className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Workflows importados</span>
          </div>
          {existingWorkflows.length === 0 ? (
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhum workflow importado ainda.</p>
          ) : (
            <ul className="space-y-2 text-xs">
              {existingWorkflows.map(w => (
                <li key={w.id} className={`flex items-center justify-between ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex flex-col">
                    <span className="font-medium">{w.name} ({(w.type || w.metadata?.type || 'desconhecido')})</span>
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{new Date(w.createdAt).toLocaleString()}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteWorkflow(w.id)}
                    className={`inline-flex items-center px-2 py-1 rounded-md border text-xs ${isDarkMode ? 'border-red-600 text-red-400 hover:bg-red-900/20' : 'border-red-600 text-red-600 hover:bg-red-50'
                      }`}
                    title="Excluir workflow"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Excluir
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {uploadStatus && (
          <div className={`p-3 rounded-lg flex items-center ${uploadStatus.type === 'success'
            ? (isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-800')
            : (isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-800')
            }`}>
            {uploadStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <XCircle className="w-5 h-5 mr-2" />
            )}
            <span className="text-sm">{uploadStatus.message}</span>
          </div>
        )}

        <div className={`p-3 rounded-lg flex items-start ${isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-800'}`}>
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium mb-1">Instruções:</p>
            <ul className="space-y-1 text-xs">
              <li>• Selecione um arquivo JSON exportado do n8n</li>
              <li>• O arquivo deve conter a configuração completa do workflow</li>
              <li>• O workflow será importado e disponibilizado para deploy</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowImporter;